// Plain text of a Word (.docx) letter, read in the browser so the file itself
// never leaves the device: only the text goes to the API (as pasted text),
// where identifiers are removed before the template is made.
// A .docx is a ZIP; word/document.xml holds the body. Uses the browser's
// DecompressionStream (deflate-raw), no extra library.

const u16 = (view: DataView, offset: number) => view.getUint16(offset, true);
const u32 = (view: DataView, offset: number) => view.getUint32(offset, true);

const inflateRaw = async (data: Uint8Array): Promise<Uint8Array> => {
  const body = new Response(data as BodyInit).body;
  if (!body) throw Error("Unreadable ZIP entry");
  const stream = body.pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

/** The bytes of one entry of a ZIP archive, or null when it is missing. */
export const readZipEntry = async (
  buffer: ArrayBuffer,
  name: string,
): Promise<Uint8Array | null> => {
  const view = new DataView(buffer);
  // End of central directory: last 22 bytes plus an optional comment.
  let end = -1;
  for (
    let i = buffer.byteLength - 22;
    i >= Math.max(0, buffer.byteLength - 65557);
    i--
  ) {
    if (u32(view, i) === 0x06054b50) {
      end = i;
      break;
    }
  }
  if (end < 0) throw Error("Not a ZIP file");
  const entries = u16(view, end + 10);
  let offset = u32(view, end + 16);
  const decoder = new TextDecoder();
  for (let index = 0; index < entries; index++) {
    if (u32(view, offset) !== 0x02014b50) throw Error("Bad ZIP directory");
    const method = u16(view, offset + 10);
    const size = u32(view, offset + 20);
    const nameLength = u16(view, offset + 28);
    const extraLength = u16(view, offset + 30);
    const commentLength = u16(view, offset + 32);
    const local = u32(view, offset + 42);
    const entry = decoder.decode(
      new Uint8Array(buffer, offset + 46, nameLength),
    );
    if (entry === name) {
      const start = local + 30 + u16(view, local + 26) + u16(view, local + 28);
      const data = new Uint8Array(buffer, start, size);
      if (method === 0) return data;
      if (method === 8) return inflateRaw(data);
      throw Error("Unsupported ZIP compression");
    }
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return null;
};

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};

/** Paragraphs of a WordprocessingML body as plain text, one per line. */
export const documentXmlText = (xml: string): string =>
  xml
    .replace(/<w:tab\/>/g, "\t")
    .replace(/<w:(?:br|cr)\b[^>]*\/>/g, "\n")
    .replace(/<\/w:p>/g, "\n")
    .replace(/<w:delText\b[^>]*>[\s\S]*?<\/w:delText>/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&(#x?[0-9a-f]+|\w+);/gi, (match, code: string) => {
      if (code.startsWith("#x"))
        return String.fromCodePoint(Number.parseInt(code.slice(2), 16));
      if (code.startsWith("#"))
        return String.fromCodePoint(Number.parseInt(code.slice(1), 10));
      return ENTITIES[code] ?? match;
    })
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const docxText = async (buffer: ArrayBuffer): Promise<string> => {
  const body = await readZipEntry(buffer, "word/document.xml");
  if (!body) throw Error("Not a Word document");
  return documentXmlText(new TextDecoder().decode(body));
};
